import { useState, useEffect } from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment-timezone'
import axios from 'axios'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import BookingForm from '../components/BookingForm'

// Set default timezone to Eastern Time
const TIMEZONE = 'America/New_York'
moment.tz.setDefault(TIMEZONE)
const localizer = momentLocalizer(moment)

const abbreviateName = (fullName) => {
  const parts = fullName.trim().split(/\s+/)
  if (parts.length < 2) return fullName
  return `${parts[0]} ${parts[parts.length - 1][0]}.`
}

const FacilityReservation = () => {
  const [bookings, setBookings] = useState([])
  const [events, setEvents] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [showBookingForm, setShowBookingForm] = useState(false)
  const [selectedFacility, setSelectedFacility] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const facilities = [
    { value: 'all', label: 'All Facilities' },
    { value: 'pavilion', label: 'Pavilion' },
    { value: 'taj_mahal', label: 'Taj Mahal' }
  ]

  // Fetch bookings from the API
  const fetchBookings = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get('/api/bookings')
      // Show both approved and pending bookings, but exclude tennis courts (they have their own calendar)
      const visibleBookings = response.data.filter(
        booking => (booking.status === 'approved' || booking.status === 'pending') &&
        !booking.facility?.startsWith('court_') &&
        booking.startDate && booking.endDate && booking.startTime && booking.endTime
      )

      // Transform bookings into calendar events (in Eastern Time)
      const calendarEvents = visibleBookings.map(booking => {
        // Combine date and time to create proper Date objects in Eastern Time
        const startStr = typeof booking.startDate === 'string' ? booking.startDate : new Date(booking.startDate).toISOString()
        const endStr = typeof booking.endDate === 'string' ? booking.endDate : new Date(booking.endDate).toISOString()
        const startDateTime = moment.tz(`${startStr.split('T')[0]} ${booking.startTime}`, 'YYYY-MM-DD HH:mm', TIMEZONE).toDate()
        const endDateTime = moment.tz(`${endStr.split('T')[0]} ${booking.endTime}`, 'YYYY-MM-DD HH:mm', TIMEZONE).toDate()

        const statusLabel = booking.status === 'pending' ? ' (Pending)' : ''

        return {
          id: booking._id,
          title: `${booking.facility === 'taj_mahal' ? 'Taj' : 'Pavilion'} - ${booking.eventType || 'Event'}${statusLabel}`,
          start: startDateTime,
          end: endDateTime,
          resource: {
            facility: booking.facility,
            contactName: booking.contactName,
            contactEmail: booking.contactEmail,
            eventType: booking.eventType,
            eventDescription: booking.eventDescription,
            status: booking.status
          }
        }
      }).filter(event => !isNaN(event.start.getTime()) && !isNaN(event.end.getTime()))

      setBookings(visibleBookings)
      setEvents(calendarEvents)
    } catch (err) {
      console.error('Error fetching bookings:', err)
      // Use empty array to allow calendar to still be interactive even if backend is down
      setBookings([])
      setEvents([])
      // Don't set error to allow users to still make reservations
      setError(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings()

    // Refresh bookings when window gains focus (to show events created in admin portal)
    const handleFocus = () => {
      console.log('Window focused - refreshing bookings...')
      fetchBookings()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  // Filter events by selected facility
  const filteredEvents = selectedFacility === 'all'
    ? events
    : events.filter(event => event.resource.facility === selectedFacility)

  // Handle slot selection (clicking on calendar)
  const handleSelectSlot = (slotInfo) => {
    setSelectedSlot({
      start: slotInfo.start,
      end: slotInfo.end
    })
    setShowBookingForm(true)
  }

  // Handle event click (clicking on existing booking)
  const handleSelectEvent = (event) => {
    // Show event details in an alert or modal (in Eastern Time)
    alert(`
      Facility: ${event.resource.facility === 'taj_mahal' ? 'Taj' : 'Pavilion'}
      Event Type: ${event.resource.eventType || 'N/A'}
      Start: ${moment.tz(event.start, TIMEZONE).format('MMMM Do YYYY, h:mm a')} ET
      End: ${moment.tz(event.end, TIMEZONE).format('MMMM Do YYYY, h:mm a')} ET
      Contact: ${event.resource.contactName ? abbreviateName(event.resource.contactName) : 'N/A'}
      ${event.resource.eventDescription ? '\nDescription: ' + event.resource.eventDescription : ''}
    `)
  }

  // Event style getter for custom styling
  const eventStyleGetter = (event) => {
    let backgroundColor
    let opacity = 0.9

    // Pending bookings show in light gray
    if (event.resource.status === 'pending') {
      backgroundColor = '#9ca3af' // light gray for pending
      opacity = 0.7
    } else {
      // Approved bookings show in facility colors matching the legend
      backgroundColor = event.resource.facility === 'taj_mahal'
        ? '#1d7156' // secondary-600 for Taj Mahal
        : '#3b5364' // primary-600 for Pavilion
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: '500'
      }
    }
  }

  const handleBookingSuccess = () => {
    setShowBookingForm(false)
    setSelectedSlot(null)
    fetchBookings() // Refresh bookings after successful submission
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section
        className="relative text-white min-h-[450px] flex items-center"
        style={{backgroundImage: 'url(/images/Pavilion_1.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/80 via-primary-800/70 to-secondary-900/80"></div>
        <div className="container-custom relative z-10 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mb-6 animate-fade-in text-4xl md:text-5xl lg:text-6xl font-bold">
              Facility Reservations
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-95">
              Reserve the Pavilion or Taj Mahal for your special events
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container-custom py-12">
        {/* Information Banner */}
        <div className="mb-8 bg-primary-50 border-l-4 border-primary-600 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-primary-900 mb-2">
            How to Reserve a Facility
          </h3>
          <ul className="text-primary-800 space-y-2">
            <li className="flex items-start">
              <span className="font-bold mr-2">1.</span>
              <span>Click on any available date/time on the calendar to open the booking form</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">2.</span>
              <span>Fill out the reservation request form with your event details</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">3.</span>
              <span>Submit your request - your time slot will be held (shown in gray) until board approval</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">4.</span>
              <span>Once approved, the reservation will appear in color on the calendar</span>
            </li>
            <li className="flex items-start">
              <span className="font-bold mr-2">5.</span>
              <span>You will receive a confirmation email when your request is reviewed</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary-200">
            <p className="text-primary-800 text-sm font-medium">
              <span className="inline-block w-3 h-3 bg-gray-400 rounded opacity-70 mr-2 align-middle"></span>
              Gray events are pending approval and temporarily hold that time slot
            </p>
          </div>
        </div>

        {/* Facility Filter */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <label htmlFor="facility-filter" className="text-sm font-semibold text-gray-700">
              Filter by Facility:
            </label>
            <select
              id="facility-filter"
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              {facilities.map(facility => (
                <option key={facility.value} value={facility.value}>
                  {facility.label}
                </option>
              ))}
            </select>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-primary-600 rounded"></div>
              <span className="text-gray-700">Pavilion</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-secondary-600 rounded"></div>
              <span className="text-gray-700">Taj Mahal</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-400 rounded opacity-70"></div>
              <span className="text-gray-700">Pending Approval</span>
            </div>
          </div>

          <button
            onClick={() => setShowBookingForm(true)}
            className="btn btn-primary"
          >
            New Reservation Request
          </button>
        </div>

        {/* Calendar Card */}
        <div className="card p-6 mb-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-600 text-lg">{error}</p>
              <button
                onClick={fetchBookings}
                className="btn btn-primary mt-4"
              >
                Retry
              </button>
            </div>
          ) : (
            <Calendar
              localizer={localizer}
              events={filteredEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: window.innerWidth < 768 ? 500 : 700 }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable
              eventPropGetter={eventStyleGetter}
              views={window.innerWidth < 768 ? ['month', 'agenda'] : ['month', 'week', 'day', 'agenda']}
              defaultView="month"
              popup
              tooltipAccessor={(event) => event.title}
              messages={{
                next: 'Next',
                previous: 'Previous',
                today: 'Today',
                month: 'Month',
                week: 'Week',
                day: 'Day',
                agenda: 'Agenda',
                showMore: (total) => `+${total} more`
              }}
            />
          )}
        </div>

        {/* Facilities Information */}
        <div className="grid md:grid-cols-2 gap-8 mt-12">
          <div className="card p-6">
            <div className="flex items-center mb-4">
              <div className="w-3 h-3 bg-primary-600 rounded-full mr-3"></div>
              <h3 className="text-2xl font-bold text-gray-900">Pavilion</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Our spacious pavilion is perfect for large gatherings, birthday parties,
              and community events. Features include covered seating, picnic tables,
              and easy access to pool facilities.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">✓</span>
                <span>Covered seating area</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">✓</span>
                <span>Picnic tables</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">✓</span>
                <span>Electrical outlets</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-600 mr-2">✓</span>
                <span>Adjacent to tennis courts</span>
              </li>
            </ul>
          </div>

          <div className="card p-6">
            <div className="flex items-center mb-4">
              <div className="w-3 h-3 bg-secondary-600 rounded-full mr-3"></div>
              <h3 className="text-2xl font-bold text-gray-900">Taj Mahal</h3>
            </div>
            <p className="text-gray-600 mb-4">
              The Taj Mahal is our premium covered facility, ideal for more intimate
              gatherings and special occasions. It offers a beautiful setting with
              modern amenities.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-secondary-600 mr-2">✓</span>
                <span>Fully enclosed structure</span>
              </li>
              <li className="flex items-start">
                <span className="text-secondary-600 mr-2">✓</span>
                <span>TVs</span>
              </li>
              <li className="flex items-start">
                <span className="text-secondary-600 mr-2">✓</span>
                <span>Kitchen access</span>
              </li>
              <li className="flex items-start">
                <span className="text-secondary-600 mr-2">✓</span>
                <span>Premium amenities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">
                Facility Reservation Request
              </h2>
              <button
                onClick={() => {
                  setShowBookingForm(false)
                  setSelectedSlot(null)
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <BookingForm
                initialSlot={selectedSlot}
                onSuccess={handleBookingSuccess}
                onCancel={() => {
                  setShowBookingForm(false)
                  setSelectedSlot(null)
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FacilityReservation
